import React, { useState } from 'react';
import { ArrowLeft, Wind, Heart, Brain, Eye, Thermometer, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';

const PrimarySurvey = ({ onNext, onBack, onSubPath, onDataChange }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [surveyData, setSurveyData] = useState({
    breathing: {},
    circulation: {},
    disability: {},
    exposure: {}
  });

  const surveyItems = [
    {
      id: 'airway',
      title: 'Airway',
      icon: <Heart className="w-7 h-7" />,
      color: 'text-red-500',
      bgColor: 'bg-red-900/30',
      borderColor: 'border-red-500',
      hasSubPath: true,
      subPathLabel: 'Assess Inhalation Injury'
    },
    {
      id: 'breathing',
      title: 'Breathing',
      icon: <Wind className="w-7 h-7" />,
      color: 'text-blue-500',
      bgColor: 'bg-blue-900/30',
      borderColor: 'border-blue-500',
      questions: [
        { id: 'resp_rate', label: 'Respiratory Rate (breaths/min)', type: 'number' },
        { id: 'oxygen_sat', label: 'Oxygen Saturation (%)', type: 'number' },
        { id: 'breath_sounds', label: 'Breath Sounds', type: 'select', options: ['Normal', 'Decreased', 'Absent', 'Wheezing'] },
        { id: 'chest_movement', label: 'Chest Movement', type: 'select', options: ['Symmetrical', 'Asymmetrical'] }
      ]
    },
    {
      id: 'circulation',
      title: 'Circulation',
      icon: <Heart className="w-7 h-7" />,
      color: 'text-pink-500',
      bgColor: 'bg-pink-900/30',
      borderColor: 'border-pink-500',
      questions: [
        { id: 'heart_rate', label: 'Heart Rate (bpm)', type: 'number' },
        { id: 'blood_pressure', label: 'Blood Pressure (mmHg)', type: 'text', placeholder: '120/80' },
        { id: 'capillary_refill', label: 'Capillary Refill', type: 'select', options: ['<2 sec', '>2 sec'] },
        { id: 'skin_color', label: 'Skin Color', type: 'select', options: ['Pink', 'Pale', 'Cyanotic', 'Mottled'] }
      ]
    },
    {
      id: 'disability',
      title: 'Disability',
      icon: <Brain className="w-7 h-7" />,
      color: 'text-purple-500',
      bgColor: 'bg-purple-900/30',
      borderColor: 'border-purple-500',
      questions: [
        { id: 'gcs_total', label: 'Glasgow Coma Scale (GCS)', type: 'number', max: 15 },
        { id: 'pupils_left', label: 'Left Pupil', type: 'select', options: ['Reactive', 'Sluggish', 'Fixed'] },
        { id: 'pupils_right', label: 'Right Pupil', type: 'select', options: ['Reactive', 'Sluggish', 'Fixed'] },
        { id: 'motor_response', label: 'Motor Response', type: 'select', options: ['Normal', 'Weakness', 'None'] }
      ]
    },
    {
      id: 'exposure',
      title: 'Exposure',
      icon: <Thermometer className="w-7 h-7" />,
      color: 'text-orange-500',
      bgColor: 'bg-orange-900/30',
      borderColor: 'border-orange-500',
      questions: [
        { id: 'temperature', label: 'Body Temperature (°C)', type: 'number', step: '0.1' },
        { id: 'hypothermia_risk', label: 'Hypothermia Risk', type: 'select', options: ['Low', 'Moderate', 'High'] },
        { id: 'visible_injuries', label: 'Other Visible Injuries', type: 'text', placeholder: 'Describe any other injuries' }
      ]
    },
    {
      id: 'burnSceneEval',
      title: 'Burn Scene Evaluation',
      icon: <Eye className="w-7 h-7" />,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-900/30',
      borderColor: 'border-yellow-500',
      hasSubPath: true,
      subPathLabel: 'Go to Scene Safety & Mechanism'
    }
  ];

  const handleSubPath = (itemId) => {
    if (itemId === 'airway') {
      onSubPath('inhalation');
    } else if (itemId === 'burnSceneEval') {
      onSubPath('sceneEval');
    }
  };

  const handleToggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const handleInputChange = (sectionId, questionId, value) => {
    const newData = {
      ...surveyData,
      [sectionId]: { ...surveyData[sectionId], [questionId]: value }
    };
    setSurveyData(newData);
    if (onDataChange) {
      onDataChange({ primary_survey: newData });
    }
  };

  const handleNext = () => {
    if (onDataChange) {
      onDataChange({ primary_survey: surveyData });
    }
    onNext();
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-black py-4">
      <div className="w-full max-w-sm mx-auto px-4">
        <div className="flex flex-col min-h-screen bg-zinc-900 rounded-3xl border-2 border-orange-500/30 shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b-2 border-orange-500/30">
            <Button onClick={onBack} variant="ghost" className="flex items-center gap-2 text-orange-500 text-lg font-bold">
              <ArrowLeft className="w-6 h-6" /> Back
            </Button>
            <h1 className="text-lg font-black text-orange-500">Primary Survey</h1>
            <div className="w-16" />
          </div>

          <main className="flex-1 overflow-y-auto p-4 space-y-3 bg-black">
            {surveyItems.map((item) => (
              <Card key={item.id} className={`${item.bgColor} ${item.borderColor} border-2 bg-zinc-900 rounded-2xl`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`${item.color}`}>{item.icon}</div>
                      <h3 className={`font-black text-xl ${item.color}`}>{item.title}</h3>
                    </div>
                    {item.questions && (
                      <button onClick={() => handleToggleSection(item.id)} className={`${item.color}`}>
                        {expandedSection === item.id ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
                      </button>
                    )}
                  </div>
                  
                  {item.hasSubPath && (
                    <div 
                      onClick={() => handleSubPath(item.id)}
                      className={`w-full mt-2 rounded-2xl ${item.bgColor} border-2 ${item.borderColor} p-4 cursor-pointer hover:opacity-80 transition-all`}
                    >
                      <p className={`text-base ${item.color} font-bold leading-relaxed`}>
                        {item.subPathLabel}
                      </p>
                    </div>
                  )}

                  {item.questions && expandedSection === item.id && (
                    <div className="mt-4 space-y-3 pt-3 border-t-2 border-orange-500/20">
                      {item.questions.map((q) => (
                        <div key={q.id}>
                          <label className="block text-base font-bold text-orange-300 mb-2">{q.label}</label>
                          {q.type === 'select' ? (
                            <select
                              value={surveyData[item.id]?.[q.id] || ''}
                              onChange={(e) => handleInputChange(item.id, q.id, e.target.value)}
                              className="w-full rounded-lg bg-zinc-800 border-2 border-orange-500/30 text-base text-orange-100 p-3 focus:border-orange-500"
                            >
                              <option value="">Select...</option>
                              {q.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          ) : (
                            <Input
                              type={q.type}
                              placeholder={q.placeholder}
                              step={q.step}
                              max={q.max}
                              value={surveyData[item.id]?.[q.id] || ''}
                              onChange={(e) => handleInputChange(item.id, q.id, e.target.value)}
                              className="w-full rounded-lg bg-zinc-800 border-2 border-orange-500/30 text-base text-orange-100 p-3 focus:border-orange-500"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </main>

          <div className="p-4 border-t-2 border-orange-500/30 bg-zinc-900">
            <Button onClick={handleNext} className="w-full h-14 bg-orange-500 text-white text-lg font-black rounded-xl hover:bg-orange-600 shadow-xl">
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrimarySurvey;